import {
  SET_RENT_LIST,
  SET_RENT_LIST_PAGE,
  SET_RENT_LIST_PAGE_TOTAL,
  SET_RENT_LIST_QUERY,
  SET_RENT_LIST_PAGE_SIZE,
  SET_RENT_LIST_SEARCHING,
  SET_RENTAL_VIEW_STATE,
  SET_RENTAL_VIEW_QUERY,
  SET_RENTAL_DASHBOARD,
} from './mutation-types'
import api from '@/api'
import {objectTrim} from '@/utils'
import { checkUserName } from '@/views/User/utils'

const initQuery = () => ({
  pageNum: 1,
  pageSize: 50,
  userId: '',
  name: '',
  category: '',
  ip: '',
  macCode: '',
  snCode: '',
  requirements: '',
  leaseUserName: '',
  status: '',
})
const createRentalQuery = type => {
  const shared = {
    pageNum: 1,
    pageSize: 50,
    ip: '',
    leaseUserName: '',
  }
  if (type === 'publicCloud') {
    return {
      ...shared,
      resourceId: '',
      instanceType: '',
      hardwareModel: '',
    }
  }
  return {
    ...shared,
    name: '',
    category: '',
    macCode: '',
    snCode: '',
    requirements: '',
    leasePlan: '',
    salesperson: '',
  }
}

const toPlatformParams = (query, includeQueryType = true) => {
  const {leasePlan, salesperson, status, ...params} = objectTrim(query)
  return {
    ...params,
    ...(leasePlan ? {leasePlanName: leasePlan} : {}),
    ...(salesperson !== '' && salesperson !== undefined ? {salesmanId: salesperson} : {}),
    ...(includeQueryType ? {queryType: 0} : {}),
  }
}

const toThirdParams = (query, queryType) => {
  const {
    resourceId,
    ip,
    leaseUserName,
    instanceType,
    hardwareModel,
    transferInStartTime,
    transferInEndTime,
    transferOutStartTime,
    transferOutEndTime,
    ...params
  } = objectTrim(query)
  return {
    ...params,
    queryType,
    ...(resourceId ? {instanceId: resourceId} : {}),
    ...(ip ? {instanceIp: ip} : {}),
    ...(leaseUserName ? {userName: leaseUserName} : {}),
    ...(instanceType ? {flavorId: instanceType} : {}),
    ...(hardwareModel ? {instanceType: hardwareModel} : {}),
    ...(transferInStartTime ? {transferInTimeStart: transferInStartTime} : {}),
    ...(transferInEndTime ? {transferInTimeEnd: transferInEndTime} : {}),
    ...(transferOutStartTime ? {transferOutTimeStart: transferOutStartTime} : {}),
    ...(transferOutEndTime ? {transferOutTimeEnd: transferOutEndTime} : {}),
  }
}

const mapPlatformItem = item => ({
  ...item,
  hardwareModel: item.hardwareModeName,
  leasePlan: item.leasePlanName,
  salesperson: item.salesmanName,
  transferInTime: item.startTime,
})

const mapThirdItem = item => ({
  ...item,
  resourceId: item.instanceId,
  ip: item.instanceIp,
  leaseUserName: item.userName,
  instanceType: item.flavorId,
  hardwareModel: item.instanceType,
  createTime: item.creationTime,
})

const mapDistribution = data => ({
  total: data ? data.totalCount || 0 : 0,
  names: data ? data.dimensionValues || [] : [],
  rows: (data ? data.rows || [] : []).map(row => ({
    hardwareModel: row.hardwareMode,
    values: Object.keys(row.dimensionCountMap || {}).map(name => ({
      name,
      count: row.dimensionCountMap[name],
    })),
  })),
})

const mapExpiringUser = item => ({
  leaseUserName: item.leaseUserName,
  salesperson: item.salesmanName,
  endLeaseTime: item.endLeaseTime,
  totalCount: item.totalCount || 0,
  hardwareSummaries: (item.hardwareSummaries || []).map(summary => ({
    hardwareModel: summary.hardwareMode,
    memory: summary.memory,
    count: summary.count || 0,
  })),
})

const createRentalView = type => ({
  loading: false,
  error: '',
  list: [],
  total: 0,
  query: createRentalQuery(type),
  dashboard: null,
})

const state = {
  searching: false,
  initQuery: initQuery(),
  rentalViews: {
    platform: createRentalView('platform'),
    publicCloud: createRentalView('publicCloud'),
  },
  rentMessage: {
    list: [],
    listPageTotal: 0,
    query: initQuery(),
  },
}

const mutations = {
  [SET_RENT_LIST] (state, payload) {
    state.rentMessage.list = payload
  },
  [SET_RENT_LIST_QUERY] (state, payload) {
    state.rentMessage.query = {
      ...state.rentMessage.query,
      ...payload,
    }
  },
  [SET_RENT_LIST_PAGE] (state, payload) {
    state.rentMessage.query.pageNum = payload
  },
  [SET_RENT_LIST_PAGE_SIZE] (state, payload) {
    state.rentMessage.query.pageSize = payload
  },
  [SET_RENT_LIST_PAGE_TOTAL] (state, payload) {
    state.rentMessage.listPageTotal = payload
  },
  [SET_RENT_LIST_SEARCHING] (state, payload) {
    state.searching = payload
  },
  [SET_RENTAL_VIEW_STATE] (state, {type, payload}) {
    state.rentalViews[type] = {
      ...state.rentalViews[type],
      ...payload,
    }
  },
  [SET_RENTAL_VIEW_QUERY] (state, {type, payload}) {
    state.rentalViews[type].query = {
      ...state.rentalViews[type].query,
      ...payload,
    }
  },
  [SET_RENTAL_DASHBOARD] (state, {type, payload}) {
    state.rentalViews[type].dashboard = payload
  },
}

const actions = {
  async queryRentalView ({commit, state}, {type, query = {}}) {
    commit(SET_RENTAL_VIEW_QUERY, {type, payload: query})
    commit(SET_RENTAL_VIEW_STATE, {type, payload: {loading: true}})
    try {
      const request = type === 'publicCloud'
        ? api.nodeApi.getThirdLeaseNodeList
        : api.nodeApi.getLeaseNodeList
      const params = type === 'publicCloud'
        ? toThirdParams(state.rentalViews[type].query, 0)
        : toPlatformParams(state.rentalViews[type].query)
      const {data} = await request(params)
      const page = data || {}
      const mapper = type === 'publicCloud' ? mapThirdItem : mapPlatformItem
      commit(SET_RENTAL_VIEW_STATE, {
        type,
        payload: {
          error: '',
          list: (page.items || []).map(mapper),
          total: page.total || 0,
          query: {
            ...state.rentalViews[type].query,
            pageNum: page.pageNum || 1,
            pageSize: page.size || state.rentalViews[type].query.pageSize,
          },
        },
      })
      return page
    } catch (error) {
      commit(SET_RENTAL_VIEW_STATE, {
        type,
        payload: {error: error.message, list: [], total: 0},
      })
      throw error
    } finally {
      commit(SET_RENTAL_VIEW_STATE, {type, payload: {loading: false}})
    }
  },
  resetRentalView ({commit, dispatch}, payload) {
    const type = typeof payload === 'string' ? payload : payload.type
    const query = typeof payload === 'string' ? {} : payload.query || {}
    commit(SET_RENTAL_VIEW_STATE, {type, payload: createRentalView(type)})
    return dispatch('queryRentalView', {type, query})
  },
  changeRentalViewQuery ({commit}, {type, query}) {
    commit(SET_RENTAL_VIEW_QUERY, {type, payload: query})
  },
  async queryRentalDashboard ({commit, state}, type) {
    try {
      if (type === 'publicCloud') {
        const {pageNum, pageSize, ...query} = state.rentalViews[type].query
        const {data} = await api.nodeApi.getThirdLeaseNodeUserDistributionStatistics(
          toThirdParams(query, 0)
        )
        const users = mapDistribution(data)
        const dashboard = {
          groupTotal: 0,
          userTotal: users.total,
          referenceTime: 0,
          groups: [],
          groupNames: [],
          users: users.rows,
          userNames: users.names,
          expiringTotal: 0,
          expiring: [],
        }
        commit(SET_RENTAL_DASHBOARD, {type, payload: dashboard})
        return dashboard
      }

      const {pageNum, pageSize, ...query} = state.rentalViews[type].query
      const filters = toPlatformParams(query, false)
      const [groupsResponse, usersResponse, expiringResponse] = await Promise.all([
        api.nodeApi.getLeaseNodeDistributionStatistics({...filters, dimension: 'category'}),
        api.nodeApi.getLeaseNodeDistributionStatistics({...filters, dimension: 'leaseUserName'}),
        api.nodeApi.getExpiringLeaseNodeUserList(filters),
      ])
      const groups = mapDistribution(groupsResponse.data)
      const users = mapDistribution(usersResponse.data)
      const expiring = (expiringResponse.data || []).map(mapExpiringUser)
      const dashboard = {
        groupTotal: groups.total,
        userTotal: users.total,
        referenceTime: expiringResponse.serverTime || Date.now(),
        groups: groups.rows,
        groupNames: groups.names,
        users: users.rows,
        userNames: users.names,
        expiringTotal: expiring.reduce((total, item) => total + item.totalCount, 0),
        expiring,
      }
      commit(SET_RENTAL_DASHBOARD, {type, payload: dashboard})
      return dashboard
    } catch (error) {
      commit(SET_RENTAL_VIEW_STATE, {type, payload: {error: error.message}})
      throw error
    }
  },
  async queryThirdLeaseNodeFilterOptions () {
    const {data} = await api.nodeApi.getThirdLeaseNodeFilterOptions({queryType: 0})
    return {
      instanceTypes: data ? data.flavorIds || [] : [],
      hardwareModels: data ? data.instanceTypes || [] : [],
      leaseUserNames: data ? data.userNames || [] : [],
    }
  },
  async queryRentalSalespersons () {
    const {data} = await api.userApi.queryAdminByOrganizationType({organizationType: 1})
    return (data || []).map(item => ({
      label: item.reallyName,
      value: item.id,
    }))
  },
  async exportRentalView ({state, rootState}, type) {
    const {pageNum, pageSize, ...query} = state.rentalViews[type].query
    if (type === 'publicCloud') {
      return api.statisticsApi.exportThirdLeaseNodeList({
        ...toThirdParams(query, 0),
        platform: rootState.home.platform,
      })
    }
    return api.statisticsApi.exportLeaseNodeDetails({
      ...toPlatformParams(query, false),
      platform: rootState.home.platform,
    })
  },
  async extendRentalEndTime ({dispatch}, payload) {
    const response = await api.nodeApi.extendLeaseEndTime(payload)
    if (response.code === 200) {
      await Promise.all([
        dispatch('queryRentalView', {type: 'platform'}),
        dispatch('queryRentalDashboard', 'platform'),
      ])
    }
    return response
  },
  async transferOutPlatformRentalNodes ({dispatch}, payload) {
    const response = await api.nodeApi.moveLeaseNodeToRenderOfficial(payload)
    if (response.code === 200) {
      await Promise.all([
        dispatch('queryRentalView', {type: 'platform'}),
        dispatch('queryRentalDashboard', 'platform'),
      ])
    }
    return response
  },
  // 获取节点机列表
  async getRentlist ({commit, state, dispatch}, payload) {
    try {
      let param = {
        ...objectTrim(state.rentMessage.query),
        ...objectTrim(payload),
      }
      if (payload && !payload.leaseUserName && param.leaseUserName) {
        delete param.leaseUserName
      }
      commit(SET_RENT_LIST_SEARCHING, true)
      const {data, code} = await api.nodeApi.getRentlist(param)
      checkUserName({
        code,
        data,
        callback: ({userId}) => {
          const { leaseUserName, ...other } = param
          dispatch('getRentlist', {
            ...other,
            userId,
          })
        },
      })
      if (data) {
        commit(SET_RENT_LIST_PAGE, data.pageNum || 1)
        commit(SET_RENT_LIST_PAGE_TOTAL, data.total || 0)
        commit(SET_RENT_LIST, data.items || [])
        commit(SET_RENT_LIST_PAGE_SIZE, data.size)
      }
    } catch (error) {
      commit(SET_RENT_LIST_PAGE_TOTAL, 0)
      commit(SET_RENT_LIST, [])
    }
    commit(SET_RENT_LIST_SEARCHING, false)
  },
  // 初始化节点机列表
  async initRentList ({dispatch}) {
    // dispatch('changeRentListPage', {currentPage: 1})
    await dispatch('changeRentMessageQuery', {
      pageNum: 1,
      pageSize: 50,
      userId: '',
      name: '',
      category: '',
      ip: '',
      macCode: '',
      snCode: '',
      requirements: '',
      leaseUserName: '',
      status: '',
    })
    await dispatch('getRentlist')
  },
  // 节点机列表翻页
  async changeRentListPage ({commit, state, dispatch}, payload) {
    commit(SET_RENT_LIST_PAGE, payload.currentPage)
    if (!payload.noXHR) await dispatch('getRentlist')
    return Promise.resolve(true)
  },
  // 改变每页显示条数
  async changePageSize ({commit, dispatch}, payload) {
    commit(SET_RENT_LIST_PAGE_SIZE, payload.listPageSize)
    return Promise.resolve(true)
  },
  // 节点机列表筛选
  changeRentMessageQuery ({commit, state, dispatch}, payload) {
    commit(SET_RENT_LIST_QUERY, payload)
  },
  // 获取所有状态
  async getNodeAllStatus () {
    const {data} = await api.nodeApi.getNodeAllStatus()
    return data
  },
  // 获取所有群组列表
  async queryCategorys () {
    const {data} = await api.nodeApi.queryCategorys()
    return data
  },
  // 获取要求
  async queryRequirements () {
    const {data} = await api.nodeApi.queryRequirements()
    return data
  },
  // 转出至渲染列表
  async moveLeaseNodeToRender ({dispatch}, payload) {
    const {result, code} = await api.nodeApi.moveLeaseNodeToRender(payload)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, code}
  },
  // 添加群组
  async addCategory ({dispatch}, payload) {
    const {result, code} = await api.nodeApi.addCategory(payload)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, code}
  },
  // 释放群组
  async deleteCategory ({dispatch}, payload) {
    const {result, code} = await api.nodeApi.deleteCategory(payload)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, code}
  },
  // 增加租赁节点机
  async addRentNode ({dispatch}, payload) {
    const {result, message, code, data} = await api.nodeApi.addRentNode(payload)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, message, code, data}
  },
  // 删除租赁节点机
  async delRentNode ({dispatch}, payload) {
    const {result, code} = await api.nodeApi.delRentNode(payload)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, code}
  },
  // 切换镜像
  async changeLeaseMirror ({dispatch}, payload) {
    const {result, code, message} = await api.nodeApi.changeLeaseMirror(payload)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, code, message}
  },
  // 获取所有租赁镜像
  async getLeaseMirrorList ({state}, payload) {
    const {data, code} = await api.nodeApi.getLeaseMirrorList(payload)
    return {data, code}
  },
  // 根据添加输入的ip获取租赁节点机的镜像
  async getLeaseSameMirrorByIp ({state}, payload) {
    const {data, code} = await api.nodeApi.getLeaseSameMirrorByIp(payload)
    return {data, code}
  },
  // 编辑SN编号
  async editRentSnCode ({commit, state, dispatch}, payload) {
    let param = {
      ...payload,
    }
    const {result, message, code} = await api.nodeApi.editRentSnCode(param)
    if (result) {
      dispatch('getRentlist')
    }
    return {result, message, code}
  },
  // 导入节点机数据
  async importAdminRentNodeData ({commit, state, dispatch}, payload) {
    const {result, message, code, data} = await api.nodeApi.importAdminRentNodeData(payload)
    if (code === 200) {
      dispatch('getRentlist')
    }
    return {result, message, code, data}
  },
  // 普通作业提交设置
  async setNormalSubmit ({state}, payload) {
    const {data, code} = await api.nodeApi.setNormalSubmit(payload)
    return {data, code}
  },
  // 获取普通作业提交设置
  async getNormalSubmit ({state}, payload) {
    const {data, code} = await api.nodeApi.getNormalSubmit(payload)
    return {data, code}
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}
